import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { ScanIdsData } from '@/models/Scans'
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import { UUID } from 'crypto'
import { useEffect, useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import MatchNeedsListItem from '@/components/MatchNeedsListItem'
import { useScanIdsByNeedId } from '@/utils/hooks/scans'
import MobileMatchPage from './mobile/MobileMatchPage'
import LoginSignup from '@/components/LoginSignup'
import { getEffectiveOrgUuid } from '@/utils/utils'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { Link } from 'react-router-dom'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

export default function MatchPage() {
  usePageTitle('Funding Opportunities')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { isOnboarding } = useOnboardingStatus()
  const focusRef = useFocusManagement()

  const [showArchived, setShowArchived] = useState(false)
  const [runScanOnAll, setRunScanOnAll] = useState(false)
  const [loading, setLoading] = useState(true)

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  const isAnnualSubscriber = organisation?.is_annual_subscriber === true

  const {
    data: fundingNeeds,
    isLoading: fundingNeedsLoading,
    error,
  } = useFundingNeeds(token, organisation?.uuid as UUID)

  const {
    data: scanIds,
    isLoading: scanIdsLoading,
    isError: scanIdsError,
  } = useScanIdsByNeedId(
    token as string,
    (fundingNeeds && fundingNeeds?.map((fundingNeed) => fundingNeed.id)) || []
  ) as { data: ScanIdsData[]; isLoading: boolean; isError: boolean }

  useEffect(() => {
    setLoading(
      userLoading || orgsLoading || fundingNeedsLoading || scanIdsLoading
    )
  }, [userLoading, orgsLoading, fundingNeedsLoading, scanIdsLoading])

  if (loading) {
    return <Loading />
  }

  if (isError || error || orgErrors.length > 0 || scanIdsError) {
    return <ErrorMessage />
  }

  const filteredNeeds = fundingNeeds?.filter(
    (need) => showArchived || !need.is_archived
  )
  const allFundingNeedsArchived = Boolean(
    fundingNeeds?.length && !fundingNeeds.some((need) => !need.is_archived)
  )

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!organisation && <ErrorMessage />)}
          {isMobile ? (
            <MobileMatchPage />
          ) : organisation &&
            !fundingNeedsLoading &&
            (!fundingNeeds || fundingNeeds.length === 0) ? (
            <Box
              sx={{
                margin: '0 auto',
                marginTop: 2,
                width: '100%',
                pb: 5,
                px: 15,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  mb: 1,
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  ref={focusRef}
                  tabIndex={-1}
                  sx={{
                    mb: 4,
                    textAlign: 'left',
                    fontSize: '34px',
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                >
                  Find Funding Opportunity Matches
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '60vw',
                  px: 6,
                  height: '20vh',
                  border: 1,
                  borderColor: '#1C3045',
                  borderRadius: 2,
                  mx: 'auto',
                  bgcolor: '#f7f7f7',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontSize: '20px',
                    textAlign: 'center',
                  }}
                >
                  {' '}
                  You don't have any Funding Requests yet!
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 1,
                    textAlign: 'center',
                    fontSize: '16px',
                  }}
                >
                  {' '}
                  Add a Funding Request on the{' '}
                  <Link to="/funding-requests" style={{ color: '#1C3045' }}>
                    Funding Requests Page{' '}
                  </Link>{' '}
                  to get started
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  margin: '0 auto',
                  marginTop: 2,
                  width: '100%',
                  pb: 5,
                  px: 15,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    mb: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    ref={focusRef}
                    tabIndex={-1}
                    sx={{
                      mb: 2,
                      textAlign: 'left',
                      fontSize: '34px',
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    Find Funding Opportunity Matches
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {/* <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'row', ml: 1.5 }}
                    > */}
                  {/* <Button
                        variant="contained"
                        disableRipple
                        disabled={runScanOnAll}
                        sx={{
                          bgcolor: '#1C3045',
                          color: 'white',
                          textTransform: 'none',
                          borderRadius: 2,
                          py: 1,
                          px: 6,
                          fontSize: '16px',
                          boxShadow: 'none',
                        }}
                        onClick={() => handleRunScanOnAll()}
                      >
                        {' '}
                        Run a Scan for all Funding Requests{' '}
                      </Button> */}
                  {/* <FormControlLabel
                        control={
                          <Checkbox
                            checked={inclRelationalAll}
                            onChange={() =>
                              setInclRelationalAll(!inclRelationalAll)
                            }
                            sx={{
                              '&.Mui-checked': {
                                color: '#1C3045',
                              },
                              ml: 2,
                            }}
                          />
                        }
                        label="Include Relational Funding Opportunities"
                      /> */}
                  {/* </Box>
                  </Box> */}
                </Box>
                {!isOnboarding && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ alignSelf: 'flex-start' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#1C3045',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                {
                                  backgroundColor: '#1C3045',
                                },
                              '& .MuiSwitch-thumb': {
                                border: showArchived
                                  ? '1px solid #1C3045'
                                  : '1px solid #666666',
                              },
                            }}
                          />
                        }
                        label="Show Archived"
                      />
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1.5 }}>
                      Archived funding requests aren't included when searching
                      for matches.
                    </Typography>
                  </Box>
                )}
                {allFundingNeedsArchived && (
                  <Alert severity="info" sx={{ width: '100%', mt: 2 }}>
                    All your funding requests are archived, so nothing is being
                    matched. Add one on the{' '}
                    <Link to="/funding-requests" style={{ color: '#1C3045' }}>
                      Funding Requests page
                    </Link>
                    , or turn on &quot;Show Archived&quot; to unarchive an
                    existing request.
                  </Alert>
                )}
                {/* <Typography
                  variant="body1"
                  sx={{ width: '100%', textAlign: 'right', fontSize: '16px' }}
                >
                  Include Relational
                </Typography> */}
                <List
                  sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: 600,
                    '& ul': { padding: 0 },
                  }}
                >
                  <Divider sx={{ mt: 1 }} />
                  {organisation &&
                    token &&
                    fundingNeeds &&
                    filteredNeeds &&
                    scanIds &&
                    filteredNeeds
                      .sort((a, b) => b.id - a.id)
                      .map((fundingNeed) => (
                        <Box key={fundingNeed.id}>
                          <MatchNeedsListItem
                            token={token}
                            scanIds={
                              scanIds.find(
                                (item) =>
                                  item && item.fundingNeedId === fundingNeed.id
                              )?.scanIds
                            }
                            fundingNeed={fundingNeed || []}
                            runScanOnAll={runScanOnAll}
                            isAnnualSubscriber={isAnnualSubscriber}
                          />
                          <Divider />
                        </Box>
                      ))}
                </List>
              </Box>
            </>
          )}
        </>
      </Authenticated>
    </>
  )
}
